import { Component, OnInit, EventEmitter, Input, Output, AfterViewInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, Form } from '@angular/forms';
import { MediaObserver, MediaChange } from '@angular/flex-layout';

import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';
import { PaymentService } from '../../services/payment.service';

import { PaymentSelectType, PaymentSelected } from '../payment-note-list/payment-note-list.component';
import { OpenNotesPaymentInfoListItem, OpenNotesPaymentInfoListGroupFooter } from 'src/app/models/payment';
import { CompanyService } from '../../services/company.service';      // 10/26/2021 RA
import { CompanyInformation } from '../../models/company';            // 10/26/2021 RA

@Component({
  selector: 'app-payment-amount',
  templateUrl: './payment-amount.component.html',
  styleUrls: ['./payment-amount.component.scss']
})
export class PaymentAmountComponent implements OnInit {
  private _paymentSelected: PaymentSelected = {
    paymentSelectType: null,
    selectedId: null,
    selectedData: []
  };

  @Input() displayTitle = false;

  @Input() displayNotices = false;

  @Input()
  set paymentSelected(paymentSelected: PaymentSelected) {
    this._paymentSelected = paymentSelected || {
      paymentSelectType: null,
      selectedId: null,
      selectedData: []
    };

    if (this._paymentSelected.selectedData.length > 1) {
      this.openNotesPaymentInfoListWithFooters = this._paymentService.addOpenNotesPaymentInfoListFooters(
        this._paymentSelected.selectedData
      );
    } else {
      this.openNotesPaymentInfoListWithFooters = this._paymentSelected.selectedData;
    }
    this.dataUpdated();
  }
  get paymentSelected(): PaymentSelected {
    return this._paymentSelected;
  }

  @Output() dataComplete = new EventEmitter<any>();
  @Output() selected = new EventEmitter<number>();

  // Have to use the following ElementDisplayStyle variables and ngStyle to show/hide table elements
  // This because the table paginator will not attach correctly otherwise - this is a work around for a known issue
  private _hideElementDisplayStyle = 'none';
  private _showElementDisplayStyle = 'inline';
  private _showElementDisplayStyleIE = 'inline-block';

  private _handleError: HandleError;
  private _logging = environment.logging;
  private companyCode: string = null;                 // 10/26/2021

  isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

  openNotesPaymentInfoListWithFooters: (OpenNotesPaymentInfoListItem | OpenNotesPaymentInfoListGroupFooter)[] = [];

  // DataTable
  tableDisplayedColumns = [
    'noteId',
    'nextPaymentDueDate',
    'noteDate',
    'noteStatus',
    'pastDueAmount',
    'lateFeeDue',
    'regularPaymentAmount',
    'totalAmountDue',
    'payoffAmount'
  ];
  tableDataSource = new MatTableDataSource<OpenNotesPaymentInfoListItem | OpenNotesPaymentInfoListGroupFooter>(
    this.openNotesPaymentInfoListWithFooters
  );

  tableDisplayStyle: string = this._hideElementDisplayStyle;
  tableDisplayStyleIE: string = this._hideElementDisplayStyle;
  paymentSelectedDataFound = false;
  recordCount = 0;

  paymentAmountForm: UntypedFormGroup;
  selectedPaymentOption = new UntypedFormControl();
  customPaymentAmount = new UntypedFormControl();

  customPaymentAmountValue = 0;
  minimumAmountDueValue = 0.01;
  maximumAmountDueValue = 0;
  totalAmountDueValue = 0;

  customPaymentAmountValueString = '';
  totalAmountDueValueString = '0.00';

  companyInformation: CompanyInformation;

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _errorHandler: ErrorHandler,
    private _paymentService: PaymentService,
    public media: MediaObserver,
    private _companyService: CompanyService      // 10/26/2021 RA
  ) {
    this._handleError = _errorHandler.createHandleError('PaymentAmountComponent');
  }

  ngOnInit() {
    this.paymentSelectedDataFound = true;
    this.tableDisplayStyle = this.paymentSelectedDataFound ? this._showElementDisplayStyle : this._hideElementDisplayStyle;
    this.tableDisplayStyleIE = this.paymentSelectedDataFound ? this._showElementDisplayStyleIE : this._hideElementDisplayStyle;
  }

  dataUpdated(): void {
    this.customPaymentAmountValue = 0;
    this.maximumAmountDueValue = 0;
    this.totalAmountDueValue = 0;

    this.companyInformation = this._companyService.getNewCompanyInformation();      // 10/26/2021 RA
    this.companyCode =  this._companyService.getCompanyCode();                      // 10/26/2021 RA

    this.customPaymentAmountValueString = '';
    this.totalAmountDueValueString = '0.00';

    for (const item of this._paymentSelected.selectedData) {
      this.customPaymentAmountValue += item.minimumAmountDue;
      this.maximumAmountDueValue += item.payoffAmount;
      this.totalAmountDueValue += item.totalAmountDue;
    }
    this.customPaymentAmountValue = Number(this.customPaymentAmountValue.toFixed(2));
    this.maximumAmountDueValue = Number(this.maximumAmountDueValue.toFixed(2));
    this.totalAmountDueValue = Number(this.totalAmountDueValue.toFixed(2));

    // this.customPaymentAmountValueString = this.customPaymentAmountValue.toFixed(2);
    this.totalAmountDueValueString = this.totalAmountDueValue.toFixed(2);

    this.createForm();

    this.recordCount = this._paymentSelected.selectedData.length;
    this.tableDataSource = new MatTableDataSource<OpenNotesPaymentInfoListItem | OpenNotesPaymentInfoListGroupFooter>(
      this.openNotesPaymentInfoListWithFooters
    );
    this.paymentSelectedDataFound = this._paymentSelected.selectedData.length > 0;
    this.tableDisplayStyle = this.paymentSelectedDataFound ? this._showElementDisplayStyle : this._hideElementDisplayStyle;
    this.tableDisplayStyleIE = this.paymentSelectedDataFound ? this._showElementDisplayStyleIE : this._hideElementDisplayStyle;

    this.dataComplete.emit(true);
  }

  createForm(): void {
    this.paymentAmountForm = this._formBuilder.group({});
    this.paymentAmountForm.addControl('selectedPaymentOption', this.selectedPaymentOption);
    this.paymentAmountForm.addControl('customPaymentAmount', this.customPaymentAmount);
    this.paymentAmountForm.setValue({
      selectedPaymentOption: 'TOTAL',
      customPaymentAmount: this.customPaymentAmountValueString
    });

    this.selectedPaymentOptionChanged(this.selectedPaymentOption.value);

    // Set Default Value Change Subscriptions
    this.paymentAmountForm.get('selectedPaymentOption').valueChanges.subscribe(val => {
      this.selectedPaymentOptionChanged(val);
    });
  }

  selectedPaymentOptionChanged(newSelectedPaymentOptionValue) {
    if (newSelectedPaymentOptionValue === 'TOTAL') {
      this.paymentAmountForm.get('customPaymentAmount').clearValidators();
      this.paymentAmountForm.get('customPaymentAmount').setValue(this.customPaymentAmountValueString);
      this.paymentAmountForm.get('customPaymentAmount').updateValueAndValidity();
    } else if (newSelectedPaymentOptionValue === 'CUSTOM') {
      this.paymentAmountForm.get('customPaymentAmount').clearValidators();
      this.paymentAmountForm
        .get('customPaymentAmount')
        .setValidators([Validators.required, Validators.min(this.minimumAmountDueValue), Validators.max(this.maximumAmountDueValue)]);
      this.paymentAmountForm.get('customPaymentAmount').updateValueAndValidity();
    }
  }

  setPaymentAmount(): void {
    if (this.paymentAmountForm.get('selectedPaymentOption').value === 'TOTAL') {
      this.selected.emit(this.totalAmountDueValue);
    } else if (this.paymentAmountForm.get('selectedPaymentOption').value === 'CUSTOM') {
      const customPaymentAmount = Number(this.paymentAmountForm.get('customPaymentAmount').value);
      this.selected.emit(customPaymentAmount);
    }
  }

  isGroupFooter(index, item): boolean {
    return item.isGroupFooter;
  }

  onRadioButtonClick(radioValue: string) {
    this.paymentAmountForm.get('selectedPaymentOption').setValue(radioValue);
  }

  // 10/26/2021 - Get Company Code
  CompanyCode_Check(): boolean {
    for (const companyLookup of environment.companyCode.legaltext ) {
      if (companyLookup.companyCode === this.companyCode ) {
        return true;
      }
    }
    return false;
  }
}
